import { useMemo, useSyncExternalStore } from "react";
import { expenseStore } from "./index";
import type { Expense, ExpenseStatus, ExpenseStore } from "./types";

function getSnapshot(): Expense[] {
  return expenseStore.list();
}

const SERVER_SNAPSHOT: Expense[] = [];
function getServerSnapshot(): Expense[] {
  return SERVER_SNAPSHOT;
}

function subscribe(listener: () => void): () => void {
  return expenseStore.subscribe(listener);
}

export interface UseExpensesFilter {
  status?: ExpenseStatus[];
}

export interface UseExpensesResult {
  expenses: Expense[];
  add: ExpenseStore["add"];
  addMany: ExpenseStore["addMany"];
  update: ExpenseStore["update"];
  setStatus: ExpenseStore["setStatus"];
  remove: ExpenseStore["remove"];
  submit: ExpenseStore["submit"];
  approve: ExpenseStore["approve"];
  reject: ExpenseStore["reject"];
  getById(id: string): Expense | undefined;
}

export function useExpenses(filter?: UseExpensesFilter): UseExpensesResult {
  const all = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const statusKey = filter?.status?.slice().sort().join("|") ?? "";

  const expenses = useMemo(() => {
    if (!filter?.status) return all;
    const allowed = new Set(filter.status);
    return all.filter((e) => allowed.has(e.status));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, statusKey]);

  const getById = useMemo(
    () => (id: string) => all.find((e) => e.id === id),
    [all],
  );

  return {
    expenses,
    add: expenseStore.add.bind(expenseStore),
    addMany: expenseStore.addMany.bind(expenseStore),
    update: expenseStore.update.bind(expenseStore),
    setStatus: expenseStore.setStatus.bind(expenseStore),
    remove: expenseStore.remove.bind(expenseStore),
    submit: expenseStore.submit.bind(expenseStore),
    approve: expenseStore.approve.bind(expenseStore),
    reject: expenseStore.reject.bind(expenseStore),
    getById,
  };
}
